import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { ProfileAction, MatchAction } from "../../store/actions";

import EditProfileBoxLabel from "../../components/label/editprofileboxlabel";
import EditProfileMultipleSelect from "../../components/dropdown/editprofilemultipleselection";
import RangeSlider from "../../components/rangeslider/rangeslider";
import PrimaryButton from "../../components/button/primarybutton";
import Spacer from "../../components/spacer/spacer";
import Loader from "../../components/loader/loader";

import withDoesntMatter from "../../utils/doesntmatter";
import { handleDoesntMatter } from "../../utils/handledoesntmatter";

import images from "../../assets";

import {
  residencyStatus,
  relocationPlan,
  countries,
  ethnicOrigin,
  maritalStatusMale,
  maritalStatusFemale,
  spokenLanguages,
  healthCondition,
  livingWithInlaws,
  foodHabit,
  religiousHistory,
  religiousSect,
  polygamy,
  dailyPrayer,
  religiousCommitment,
} from "../../utils/arrayitems";

import TabHeading from "./tabheading";

import "./styles.css";

const BasicInfo = (props) => {
  const dispatch = useDispatch();
  const profileLoading = useSelector((state) => state?.profile?.isLoading);
  const matchLoading = useSelector((state) => state?.matches?.isLoading);
  const profileData = useSelector((state) => state?.profile?.profile);

  const [isDisabled, setDisabled] = useState(false);
  const [state, setState] = useState({
    ageMin: 18,
    ageMax: 30,
    country: [""],
    residencyStatus: [""],
    relocationPlan: [""],
    ethnicOrigin: [""],
    maritalStatus: [""],
    spokenLanguage: [""],
    physicalStatus: [""],
    foodHabit: [""],
    livingWithInlaws: [""],
    religiousHistory: [""],
    religiousSect: [""],
    polygamy: [""],
    dailyPrayer: [""],
    religiousCommitment: [""],
  });

  useEffect(() => {
    setState({
      ...state,
      ageMin: profileData?.matchPreference?.ageMin ?? 18,
      ageMax: profileData?.matchPreference?.ageMax ?? 30,
      country: profileData?.matchPreference?.country ?? [""],
      residencyStatus: profileData?.matchPreference?.residencyStatus ?? [""],
      relocationPlan: profileData?.matchPreference?.relocationPlan ?? [""],
      ethnicOrigin: profileData?.matchPreference?.ethnicOrigin ?? [""],
      maritalStatus: profileData?.matchPreference?.maritalStatus ?? [""],
      spokenLanguage: profileData?.matchPreference?.spokenLanguage ?? [""],
      physicalStatus: profileData?.matchPreference?.physicalStatus ?? [""],
      foodHabit: profileData?.matchPreference?.foodHabit ?? [""],
      livingWithInlaws: profileData?.matchPreference?.livingWithInlaws ?? [""],
      religiousHistory: profileData?.matchPreference?.religiousHistory ?? [""],
      religiousSect: profileData?.matchPreference?.religiousSect ?? [""],
      polygamy: profileData?.matchPreference?.polygamy ?? [""],
      dailyPrayer: profileData?.matchPreference?.dailyPrayer ?? [""],
      religiousCommitment: profileData?.matchPreference
        ?.religiousCommitment ?? [""],
    });
  }, [profileLoading]);

  const handleSubmit = () => {
    setDisabled(true);
    const payload = {
      username: profileData?.username,
      matchPreference: { ...profileData?.matchPreference, ...state },
    };

    dispatch(
      MatchAction.updateMatchPreference(payload, () => {
        setDisabled(false);

        props?.handleTab("bodyPreference");
      })
    );
  };

  const handleMultiSelectChange = (key, values) => {
    if (values.includes("")) {
      setState((prev) => ({ ...prev, [key]: [""] }));
    } else {
      const cleaned = values.filter((v) => v.value !== "");
      setState((prev) => ({ ...prev, [key]: cleaned }));
    }
  };

  return (
    <>
      <TabHeading
        image={images.matchPrefBasic}
        title="Basic Information"
        subtitle="Tell us a little about your preferences."
      />
      <div className="edit-profile-content">
        {profileLoading || matchLoading ? (
          <Loader />
        ) : (
          <>
            {/* Age Range */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Age Range" />
            </div>
            <RangeSlider
              onChange={(e) =>
                setState({ ...state, ageMin: e[0], ageMax: e[1] })
              }
              value={[state.ageMin, state.ageMax] || ""}
            />

            <Spacer height="12px" />

            {/* Country */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Country" />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Country"
              data={withDoesntMatter(countries)}
              onChange={(e) => handleDoesntMatter(setState, "country", e)}
              value={state.country || ""}
            />

            <Spacer height="12px" />

            {/* Residency Status */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Residency Status" />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Residency Status"
              data={withDoesntMatter(residencyStatus)}
              onChange={(e) =>
                handleDoesntMatter(setState, "residencyStatus", e)
              }
              value={state.residencyStatus || ""}
            />

            <Spacer height="12px" />

            {/* Relocation Plan */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Relocation Plan" />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Relocation Plan"
              data={withDoesntMatter(relocationPlan)}
              onChange={(e) =>
                handleDoesntMatter(setState, "relocationPlan", e)
              }
              value={state.relocationPlan || ""}
            />

            <Spacer height="12px" />

            {/* Ethnic Origin */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Ethnic Origin" />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Ethnic Origin"
              data={withDoesntMatter(ethnicOrigin)}
              onChange={(e) => handleDoesntMatter(setState, "ethnicOrigin", e)}
              value={state.ethnicOrigin || ""}
            />

            <Spacer height="12px" />
            {/* Marital Status */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Marital Status" />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Marital Status"
              data={withDoesntMatter(
                profileData?.gender === "male"
                  ? maritalStatusFemale
                  : maritalStatusMale
              )}
              onChange={(e) => handleDoesntMatter(setState, "maritalStatus", e)}
              value={state.maritalStatus || ""}
            />

            <Spacer height="12px" />

            {/* Preferred Spoken Languages */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Preferred Spoken Languages" />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Preferred Spoken Languages"
              data={withDoesntMatter(spokenLanguages)}
              onChange={(e) =>
                handleDoesntMatter(setState, "spokenLanguage", e)
              }
              value={state.spokenLanguage || ""}
            />

            <Spacer height="12px" />

            {/* Physical Status */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Physical Status" />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Physical Status"
              data={withDoesntMatter(healthCondition)}
              onChange={(e) =>
                handleDoesntMatter(setState, "physicalStatus", e)
              }
              value={state.physicalStatus || ""}
            />

            <Spacer height="12px" />

            {/* Food Habit */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Food Habit" />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Food Habit"
              data={withDoesntMatter(foodHabit)}
              onChange={(e) => handleDoesntMatter(setState, "foodHabit", e)}
              value={state.foodHabit || ""}
            />

            <Spacer height="12px" />

            {/* Living with In-laws */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Living with In-laws" />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Living with In-laws"
              data={withDoesntMatter(livingWithInlaws)}
              onChange={(e) =>
                handleDoesntMatter(setState, "livingWithInlaws", e)
              }
              value={state.livingWithInlaws || ""}
            />

            <Spacer height="12px" />

            {/* Religious History */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Religious History" />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Religious History"
              data={withDoesntMatter(religiousHistory)}
              onChange={(e) =>
                handleDoesntMatter(setState, "religiousHistory", e)
              }
              value={state.religiousHistory || ""}
            />

            <Spacer height="12px" />

            {/* Religious Sect */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Religious Sect" />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Religious Sect"
              data={withDoesntMatter(religiousSect)}
              onChange={(e) => handleDoesntMatter(setState, "religiousSect", e)}
              value={state.religiousSect || ""}
            />

            <Spacer height="12px" />

            {/* Polygamy */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Polygamy" />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Polygamy"
              data={withDoesntMatter(polygamy)}
              onChange={(e) => handleDoesntMatter(setState, "polygamy", e)}
              value={state.polygamy || ""}
            />

            <Spacer height="12px" />

            {/* Daily Prayer */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Daily Prayer" />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Daily Prayer"
              data={withDoesntMatter(dailyPrayer)}
              onChange={(e) => handleDoesntMatter(setState, "dailyPrayer", e)}
              value={state.dailyPrayer || ""}
            />

            <Spacer height="12px" />

            {/* Religious Commitment Level */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Religious Commitment Level" />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Religious Commitment Level"
              data={withDoesntMatter(religiousCommitment)}
              onChange={(e) =>
                handleDoesntMatter(setState, "religiousCommitment", e)
              }
              value={state.religiousCommitment || ""}
            />
          </>
        )}
        <Spacer height="32px" />
        <PrimaryButton
          text="Save & Continue"
          onClick={handleSubmit}
          disabled={isDisabled}
        />
      </div>
    </>
  );
};

export default BasicInfo;
